/* eslint-disable no-unused-vars */
import { useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import { Button as ButtonAntd } from "antd";
import Button from "components/common/Button";
import Constants from "helpers/constants";
import Colors from "assets/Colors";
import useItemMutations from "graphql/useItemMutations";
import * as Yup from "yup";
import { BSON } from "realm-web";
import { useMessaging } from "context/MessagingContext";
import { useTranslation } from "react-i18next";
import { stringLPad } from "../../../helpers/utils";
import useItems, { useStockMasters } from "../../../graphql/useItems";
import s from "./styles";
import GeneralBody from "./GeneralBody";
import Header from "./Header";
import InventoryBody from "./InventoryBody";
import Mix_Match from "./Mix&Match";
import InventoryAdvanced from "./InventoryAdvanced";
import Modifiers from "./Modifiers";
import { useBusinessContext } from "../../../BusinessApp";
import PricesPerVariant from "./PricesPerVariant";
import _ from "lodash";

/**
 * creates an automatic SKU
 * @param skuList
 */
function createSku(skuList) {
  let idx = 1;
  let currentSku = `I${stringLPad(`${idx}`, 5)}`;
  const skus = skuList.sort((sku1, sku2) => sku1.localeCompare(sku2));
  skus.forEach((sku) => {
    if (sku === currentSku) {
      currentSku = `I${stringLPad(`${++idx}`, 5)}`;
    }
  });
  return currentSku;
}

function ItemForm({
  closeModal,
  storeId,
  item,
  onUpdateSuccess,
  onAddSuccess,
  onDeleteRequest,
}) {
  const { t } = useTranslation();
  const { addItem, updateItem } = useItemMutations();
  const { items, loading: loading1 } = useItems();
  const { stockMasters } = useStockMasters(storeId);

  const [section, setSection] = useState(Constants.GENERAL_SECTION);
  const [dbLoading, setDbLoading] = useState(false);
  const loading = loading1 || dbLoading;

  delete item?.mixAndMatch?.__typename;

  const [showAdvancedInventory, setShowAdvancedInventory] = useState(
    item?.stockMode || "none"
  );

  const { setErrorMessage } = useMessaging();

  const stock = stockMasters?.filter(
    (itemStockMaster) => itemStockMaster?.ownerId === item?._id
  );

  const skuList = useMemo(
    () =>
      items
        .filter((itemToFilter) => !!itemToFilter.sku)
        .map((itemToMap) => itemToMap.sku),
    [items]
  );
  const suggestedSku = useMemo(() => createSku(skuList), [skuList]);

  function removeTypename(data) {
    if (Array.isArray(data)) {
      return data.map(removeTypename);
    } else if (_.isObject(data)) {
      const cleanedData = _.omit(data, "__typename");
      return _.mapValues(cleanedData, removeTypename);
    }
    return data;
  }

  const {
    handleSubmit,
    handleChange,
    values,
    setFieldValue,
    errors,
    touched,
    isValid,
    dirty,
    getFieldProps,
  } = useFormik({
    initialValues: {
      _id: item?._id || new BSON.ObjectId(),
      name: item?.name || "",
      invoiceName: item?.invoiceName || "",
      description: item?.description || "",
      tags: (item?.tags && [...item.tags]) || [],
      subcategoryId: item?.subcategoryId || "",
      tax1: item?.tax1,
      tax2: item?.tax2,
      tax3: item?.tax3,
      tax4: item?.tax4,

      price: item?.price || 0,

      minStock: item?.minStock?.toString() || "0",

      variantId: item?.variantId,
      stockMode: item?.stockMode || "none",
      sku: item?.sku || suggestedSku,
      gtin: item?.gtin || "",
      barcode: item?.barcode || "",
      barcode2: item?.barcode2 || "",

      variantConfig: item?.variantConfig || null,

      favorite: item?.favorite || false,
      includesCommission: item?.includesCommission || false,
      disabled: item?.disabled || false,

      image: item?.image ? `${item.image}?upd=${item.updateDatetime}` : null,
      color: item?.color || null,
      alwaysAskPrice: item?.alwaysAskPrice || false,
      pinToStart: item?.pinToStart || false,
      requirePreparation: item?.requirePreparation || false,
      modifierConfig: item?.modifierConfig,
      type: item?.type || "common",
      measureUnit: item?.measureUnit || "unit",
      mixAndMatch: item?.mixAndMatch || null,
      hidden: item?.hidden || false,
      creationDatetime: item?.creationDatetime || new Date(),
      updateDatetime: new Date(),
    },
    onSubmit: async ({
      categorySelectedFor,
      maxPrice,
      minPrice,
      subCategorySelectedFor,
      mixAndMatch,
      ...values
    }) => {
      values.price = `${values.price}`;

      if (
        values.variantConfig?.variants &&
        values.variantConfig?.variants.length > 0
      ) {
        values.variantConfig.variants = values.variantConfig.variants.map(
          (variant) => ({
            ...variant,
            price: `${variant.price}`,
          })
        );
      }

      if (mixAndMatch?.steps) {
        const updatedMixAndMatch = {
          ...mixAndMatch,
          steps: mixAndMatch.steps.map((step) => ({
            ...step,
            items: step.items.map((item) => ({
              ...item,
              price: item.price || "0",
            })),
          })),
        };

        values.mixAndMatch = updatedMixAndMatch;
      }

      values.updateDatetime =
        values.updateDatetime instanceof Date
          ? values.updateDatetime.toISOString()
          : new Date().toISOString();

      const cleanedValues = removeTypename(values);
      console.log("🚀 ~ cleanedValues:", cleanedValues);

      if (!item) {
        setDbLoading(true);
        addItem(values)
          .then(() => {
            setDbLoading(false);
            closeModal();
            onAddSuccess();
          })
          .catch((e) => {
            setDbLoading(false);
            setErrorMessage({ error: e });
          });
      } else {
        setDbLoading(true);
        updateItem(item, cleanedValues)
          .then(() => {
            setDbLoading(false);
            closeModal();
            onUpdateSuccess();
          })
          .catch((e) => {
            setDbLoading(false);
            setErrorMessage({ error: e });
          });
      }
    },
    validationSchema: Yup.object({
      name: Yup.string().required(),
      sku: Yup.string().required(),
      price: Yup.string().required(),
      tax1: Yup.string().required(),
      subcategoryId: Yup.string().required(),
      mixAndMatch: Yup.object({
        steps: Yup.array().of(
          Yup.object({
            items: Yup.array().of(
              Yup.object({
                items: Yup.array()
                  .min(1, t("formValidations.requiredOneElement"))
                  .required(),
                quantity: Yup.string().required(),
                price: Yup.string().default("0"),
              })
            ),
          })
        ),
      }).nullable(),
    }),
  });
  const skuExists = useMemo(() => {
    if (item) {
      // is updating an item
      const repeatedSku = skuList.filter(
        (item) => item === values.sku && values.sku.trim()
      );
      if (repeatedSku.length === 1) {
        return false;
      }
      if (repeatedSku.length > 1) {
        return skuList.includes(values.sku.trim());
      }
    } else {
      return skuList.includes(values.sku.trim());
    }
    return false;
  }, [item, skuList, values.sku]);

  useEffect(() => {
    if (item?.variantId && item.variantId !== values.variantId) {
      setFieldValue("variantConfig.variants", []);
    }
  }, [item?.variantId, values.variantId, setFieldValue]);

  const saveButtonDisabled = !isValid || !dirty || skuExists;

  return (
    <>
      <Header
        updating={!!item}
        section={section}
        setSection={setSection}
        closeModal={closeModal}
        saveButtonDisabled={saveButtonDisabled}
        onSave={handleSubmit}
        enable={isValid && dirty}
        showAdvancedInventory={showAdvancedInventory}
        type={values.type}
        errors={errors}
        loading={loading}
        values={values}
      />
      <s.BodyContainer>
        {section === Constants.GENERAL_SECTION && (
          <GeneralBody
            handleChange={handleChange}
            setFieldValue={setFieldValue}
            values={values}
            skuExists={skuExists}
            getFieldProps={getFieldProps}
            errors={errors}
            touched={touched}
          />
        )}

        {section === Constants.INVENTORY_SECTION && (
          <InventoryBody
            handleChange={handleChange}
            setFieldValue={setFieldValue}
            setShowAdvancedInventory={setShowAdvancedInventory}
            items={items}
            stockMasters={stockMasters}
            showAdvancedInventory={showAdvancedInventory}
            values={values}
          />
        )}
        {section === Constants.MIX_AND_MATCH_SECTION && (
          <Mix_Match
            items={items}
            setFieldValue={setFieldValue}
            values={values}
            errors={errors}
          />
        )}

        {section === Constants.PRICES_PER_VARIANT_SECTION && (
          <PricesPerVariant
            setFieldValue={setFieldValue}
            values={values}
            stock={stock}
            variantId={values.variantId}
            items={items}
          />
        )}

        {section === Constants.ADVANCED_INVENTORY_SECTION && (
          <InventoryAdvanced
            setFieldValue={setFieldValue}
            values={values}
            stock={stock}
            variantId={values.variantId}
            items={items}
          />
        )}

        {section === Constants.MODIFIERS_SECTION && (
          <Modifiers
            setFieldValue={setFieldValue}
            values={values}
            stock={stock}
            variantId={values.variantId}
            items={items}
          />
        )}
        <s.BottomButtonsContainer>
          {!!item && (
            <ButtonAntd
              type="text"
              disabled={loading}
              style={{ color: Colors.notification_failed }}
              onClick={() => {
                onDeleteRequest(item);
              }}
            >
              {t("containers.InventoryItems.AddItem.deleteItem")}
            </ButtonAntd>
          )}
          <Button
            disabled={saveButtonDisabled}
            loading={loading}
            type="primary"
            style={{
              background: saveButtonDisabled
                ? `${Colors.primary}30`
                : Colors.primary,
              color: Colors.text_on_secondary,
              width: "169px",
              fontWeight: "500",
              border: "none",
              borderRadius: "5px",
              height: "40px",
            }}
            onClick={handleSubmit}
          >
            {t("containers.InventoryItems.AddItem.save")}
          </Button>
        </s.BottomButtonsContainer>
      </s.BodyContainer>
    </>
  );
}

export default ItemForm;
